import { useEffect, useMemo, useState } from 'react';
import {
  fetchApiKeys,
  fetchPermissions,
  createApiKey,
  updateApiKey,
  revokeApiKey,
  activateApiKey,
  deleteApiKey,
} from '../../services/apiKeyService';

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return '—';
  }
};

const maskKey = (key) => {
  if (!key) return '';
  if (key.length <= 16) return key;
  return `${key.slice(0, 8)}…${key.slice(-6)}`;
};

const isExpired = (expiresAt) => {
  if (!expiresAt) return false;
  return new Date(expiresAt).getTime() < Date.now();
};

function StatusBadge({ apiKey }) {
  if (!apiKey.isActive) {
    return <span className="px-2 py-0.5 text-xs rounded bg-gray-200 text-gray-700">Revoked</span>;
  }
  if (isExpired(apiKey.expiresAt)) {
    return <span className="px-2 py-0.5 text-xs rounded bg-orange-100 text-orange-700">Expired</span>;
  }
  return <span className="px-2 py-0.5 text-xs rounded bg-green-100 text-green-700">Active</span>;
}

function KeyCell({ apiKey }) {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(apiKey.key);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  return (
    <div className="flex items-center gap-2">
      <code className="text-xs bg-gray-100 px-2 py-1 rounded font-mono">
        {revealed ? apiKey.key : maskKey(apiKey.key)}
      </code>
      <button
        type="button"
        onClick={() => setRevealed((v) => !v)}
        className="text-xs text-blue-600 hover:underline"
      >
        {revealed ? 'Hide' : 'Show'}
      </button>
      <button
        type="button"
        onClick={copy}
        className="text-xs text-blue-600 hover:underline"
      >
        {copied ? 'Copied!' : 'Copy'}
      </button>
    </div>
  );
}

function ApiKeyForm({ initial, permissions, onSubmit, onCancel, submitting }) {
  const [name, setName] = useState(initial?.name || '');
  const [selectedPerms, setSelectedPerms] = useState(initial?.permissions || []);
  const [expiresAt, setExpiresAt] = useState(
    initial?.expiresAt ? new Date(initial.expiresAt).toISOString().slice(0, 10) : ''
  );
  const [error, setError] = useState(null);

  const toggle = (perm) => {
    setSelectedPerms((prev) =>
      prev.includes(perm) ? prev.filter((p) => p !== perm) : [...prev, perm]
    );
  };

  const submit = (e) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('El nombre es obligatorio');
      return;
    }
    if (selectedPerms.length === 0) {
      setError('Selecciona al menos un permiso');
      return;
    }
    onSubmit({
      name: name.trim(),
      permissions: selectedPerms,
      expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
    });
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div>
        <label className="block text-xs text-gray-600 mb-1">Name</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Cron Job Prod"
          className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
          autoFocus
        />
      </div>

      <div>
        <label className="block text-xs text-gray-600 mb-1">Permissions</label>
        <div className="space-y-1 border border-gray-200 rounded p-2 bg-gray-50">
          {permissions.map((perm) => (
            <label key={perm} className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={selectedPerms.includes(perm)}
                onChange={() => toggle(perm)}
              />
              <code className="font-mono text-xs">{perm}</code>
              {perm === '*' && (
                <span className="text-xs text-gray-500">— full access</span>
              )}
            </label>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-xs text-gray-600 mb-1">
          Expires at <span className="text-gray-400">(optional)</span>
        </label>
        <input
          type="date"
          value={expiresAt}
          onChange={(e) => setExpiresAt(e.target.value)}
          className="border border-gray-300 rounded px-3 py-2 text-sm"
        />
      </div>

      {error && <div className="text-sm text-red-600">{error}</div>}

      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="px-3 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {submitting ? 'Saving…' : initial ? 'Save changes' : 'Create key'}
        </button>
      </div>
    </form>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h2 className="text-sm font-semibold text-gray-800">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

export default function AdminApiKeys() {
  const [keys, setKeys] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [items, perms] = await Promise.all([fetchApiKeys(), fetchPermissions()]);
      setKeys(items);
      setPermissions(perms);
    } catch (err) {
      setError(err.message || 'Error loading API Keys');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async (payload) => {
    setSubmitting(true);
    try {
      const created = await createApiKey(payload);
      setKeys((prev) => [created, ...prev]);
      setShowCreate(false);
    } catch (err) {
      alert(err.message || 'Error creating API Key');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (payload) => {
    if (!editing) return;
    setSubmitting(true);
    try {
      const updated = await updateApiKey(editing.id, payload);
      setKeys((prev) => prev.map((k) => (k.id === updated.id ? updated : k)));
      setEditing(null);
    } catch (err) {
      alert(err.message || 'Error updating API Key');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (apiKey) => {
    try {
      const updated = apiKey.isActive
        ? await revokeApiKey(apiKey.id)
        : await activateApiKey(apiKey.id);
      setKeys((prev) => prev.map((k) => (k.id === updated.id ? updated : k)));
    } catch (err) {
      alert(err.message || 'Error updating status');
    }
  };

  const handleDelete = async (apiKey) => {
    if (!window.confirm(`Eliminar definitivamente la API Key "${apiKey.name}"?`)) return;
    try {
      await deleteApiKey(apiKey.id);
      setKeys((prev) => prev.filter((k) => k.id !== apiKey.id));
    } catch (err) {
      alert(err.message || 'Error deleting API Key');
    }
  };

  const rows = useMemo(() => keys, [keys]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-gray-800">API Keys</h1>
          <p className="text-xs text-gray-500">
            Manage API Keys used to access <code className="font-mono">/api/v1/*</code>
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className="px-3 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          + New API Key
        </button>
      </div>

      {loading && <div className="text-gray-500">Loading…</div>}
      {error && <div className="text-red-600 text-sm">{error}</div>}

      {!loading && !error && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
              <tr>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Key</th>
                <th className="px-3 py-2 text-left">Permissions</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Expires</th>
                <th className="px-3 py-2 text-left">Last used</th>
                <th className="px-3 py-2 text-left">Created</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-3 py-6 text-center text-gray-400">
                    No API Keys yet
                  </td>
                </tr>
              )}
              {rows.map((apiKey) => (
                <tr key={apiKey.id} className="border-t border-gray-100">
                  <td className="px-3 py-2 font-medium">{apiKey.name}</td>
                  <td className="px-3 py-2">
                    <KeyCell apiKey={apiKey} />
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {(apiKey.permissions || []).map((p) => (
                        <code
                          key={p}
                          className="text-xs bg-gray-100 px-1.5 py-0.5 rounded font-mono"
                        >
                          {p}
                        </code>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <StatusBadge apiKey={apiKey} />
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600">
                    {apiKey.expiresAt ? formatDate(apiKey.expiresAt) : '∞'}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600">
                    {formatDate(apiKey.lastUsedAt)}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600">
                    {formatDate(apiKey.createdAt)}
                  </td>
                  <td className="px-3 py-2 text-right space-x-2 whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => setEditing(apiKey)}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => handleToggleActive(apiKey)}
                      className="text-xs text-orange-600 hover:underline"
                    >
                      {apiKey.isActive ? 'Revoke' : 'Activate'}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(apiKey)}
                      className="text-xs text-red-600 hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <Modal title="Create API Key" onClose={() => setShowCreate(false)}>
          <ApiKeyForm
            permissions={permissions}
            onSubmit={handleCreate}
            onCancel={() => setShowCreate(false)}
            submitting={submitting}
          />
        </Modal>
      )}

      {editing && (
        <Modal title={`Edit "${editing.name}"`} onClose={() => setEditing(null)}>
          <ApiKeyForm
            initial={editing}
            permissions={permissions}
            onSubmit={handleUpdate}
            onCancel={() => setEditing(null)}
            submitting={submitting}
          />
        </Modal>
      )}
    </div>
  );
}
